import { useCallback, useEffect, useState } from 'react';
import { BACKEND_URL } from '../config';
import type { Session } from '../types/app';
import type { MaintenanceState, SystemStatus } from '../types/system';
import '../styles/system-status.css';

function adminFetch(url: string, pseudo: string, options: RequestInit = {}): Promise<Response> {
  return fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      'X-Admin-Pseudo': pseudo,
    },
  });
}

interface AdminMaintenancePanelProps {
  session: Session;
}

export default function AdminMaintenancePanel({ session }: AdminMaintenancePanelProps) {
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [maintenance, setMaintenance] = useState<MaintenanceState | null>(null);
  const [message, setMessage] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    const res = await adminFetch(
      `${BACKEND_URL}/api/admin/system/status?pseudo=${encodeURIComponent(session.pseudo)}`,
      session.pseudo,
    );
    if (!res.ok) return;
    const data = await res.json() as SystemStatus;
    setStatus(data);
    setMaintenance(data.maintenance);
    setMessage(data.maintenance.message);
    setEndsAt(data.maintenance.endsAt ? data.maintenance.endsAt.slice(0, 16) : '');
  }, [session.pseudo]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const saveMaintenance = async (enabled: boolean) => {
    setBusy(true);
    setFeedback(null);
    try {
      const res = await adminFetch(
        `${BACKEND_URL}/api/admin/system/maintenance?pseudo=${encodeURIComponent(session.pseudo)}`,
        session.pseudo,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            enabled,
            message: message.trim() || undefined,
            endsAt: endsAt ? new Date(endsAt).toISOString() : null,
          }),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur');
      setMaintenance(data.maintenance);
      setFeedback(enabled ? 'Maintenance activée' : 'Maintenance désactivée');
      await loadAll();
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  if (!status || !maintenance) {
    return <div className="admin-empty">Chargement…</div>;
  }

  return (
    <div className="admin-system-ops">
      <p className="admin-system-ops-meta">
        Environnement <strong>{status.appEnv}</strong> · build {status.buildId}
      </p>

      <div className="admin-system-ops-section">
        <h4>Message affiché aux joueurs</h4>
        <label htmlFor="maintenance-message">Message</label>
        <textarea
          id="maintenance-message"
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Mise à jour en cours — reprise dans quelques minutes."
        />

        <div className="admin-system-ops-row" style={{ marginTop: '0.5rem' }}>
          <label htmlFor="maintenance-ends-at">Reprise prévue (optionnel)</label>
          <input
            id="maintenance-ends-at"
            type="datetime-local"
            value={endsAt}
            onChange={e => setEndsAt(e.target.value)}
          />
        </div>

        <div className="admin-system-ops-row">
          <button
            type="button"
            className="admin-btn-sm admin-btn-primary"
            disabled={busy || maintenance.enabled}
            onClick={() => void saveMaintenance(true)}
          >
            Activer maintenance
          </button>
          <button
            type="button"
            className="admin-btn-sm"
            disabled={busy || !maintenance.enabled}
            onClick={() => void saveMaintenance(false)}
          >
            Désactiver
          </button>
          {maintenance.enabled && (
            <span className="admin-system-ops-meta" style={{ color: '#fbbf24' }}>
              Maintenance active
            </span>
          )}
        </div>
      </div>

      {feedback && <p className="admin-system-ops-meta admin-system-ops-feedback">{feedback}</p>}
    </div>
  );
}
